import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import {
  type IJBPPlanning,
  isCanonicalPlanningRow,
  isPlanningPlanRow,
} from 'partner360/entities/planning'
import { isPlanningCategoryDisabled } from 'partner360/entities/planning/model/isPlanningCategoryDisabled'
import { getActualsValueTypeDefault } from 'partner360/helpers/sectionSettings/getActualsValueTypeDefault'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type { IPlanningMeasureItem, IPlanningMeasureQuality } from '../model/planningMeasure'
import type { TPlanReviewOffering } from '../model/types'

interface IGetPlanningMeasureQualityParams {
  rows: readonly IJBPPlanning[]
  partnerId: string
  fYear: number
  programType: TPlanReviewOffering
  isDistributor: boolean
  actualsTypeDefault?: ActualsValueTypes
  sectionSettings: Record<string, unknown>
}

/** Read saved metadata, never the current AVT switch or the PDF export's saved override. */
export const getPlanningMeasureQuality = ({
  rows,
  partnerId,
  fYear,
  programType,
  isDistributor,
  actualsTypeDefault,
  sectionSettings,
}: IGetPlanningMeasureQualityParams): IPlanningMeasureQuality => {
  // Keyed by offering and category: a review across every offering holds several offerings'
  const firstTargetByCategory = new Map<
    string,
    { offering: string; category: string; row: IJBPPlanning }
  >()

  rows.forEach((row) => {
    const offering = row.ProgramTypes?.trim()

    if (
      row.PartnerId !== partnerId ||
      !offering ||
      (programType !== null && offering !== programType) ||
      !isPlanningPlanRow(row, fYear) ||
      !isCanonicalPlanningRow(row, { excludeAuxiliaryRows: true }) ||
      row.IsNoPlan ||
      isPlanningCategoryDisabled(row.Category ?? '', sectionSettings)
    ) {
      return
    }

    // Planning restores the switch from the first target. A category can be saved independently.
    const category = row.Category?.trim() ?? ''
    const key = `${offering}\0${category}`
    if (category && !firstTargetByCategory.has(key)) {
      firstTargetByCategory.set(key, { offering, category, row })
    }
  })

  const defaultActualsType =
    actualsTypeDefault ||
    getActualsValueTypeDefault(programType === ProgramTypes.Retail, isDistributor)
  const items: IPlanningMeasureItem[] = []

  firstTargetByCategory.forEach(({ offering, category, row }) => {
    const actualsType = Object.values(ActualsValueTypes).find(
      (value) => value && value.toLowerCase() === row.NetNdp?.trim().toLowerCase()
    )
    if (actualsType) {
      items.push({ offering, category, actualsType, defaultActualsType })
    }
  })

  const issues = items.filter((item) => item.actualsType !== item.defaultActualsType)

  return {
    defaultActualsType,
    score: items.length ? (issues.length ? 0 : 100) : null,
    band: items.length ? (issues.length ? 'Low' : 'High') : null,
    items,
    issues,
  }
}
